import React, { createContext, useContext, useState, ReactNode } from 'react';

export type ApiRequestModalMode = 'config' | 'test';

interface ApiRequestModalState {
  isOpen: boolean;
  mode: ApiRequestModalMode | null;
  datasetId: number | null;
  sourceId?: number;
  method?: string;
}

interface ApiRequestModalContextType {
  modalState: ApiRequestModalState;
  openConfigModal: (datasetId: number, sourceId?: number, method?: string) => void;
  openTestModal: (datasetId?: number, sourceId?: number, method?: string) => void;
  switchMode: (mode: ApiRequestModalMode) => void;
  closeModal: () => void;
}

const ApiRequestModalContext = createContext<ApiRequestModalContextType | undefined>(undefined);

interface ApiRequestModalProviderProps {
  children: ReactNode;
}

export const ApiRequestModalProvider: React.FC<ApiRequestModalProviderProps> = ({ children }) => {
  const [modalState, setModalState] = useState<ApiRequestModalState>({
    isOpen: false,
    mode: null,
    datasetId: null,
    sourceId: undefined,
    method: undefined,
  });

  const openConfigModal = (datasetId: number, sourceId?: number, method?: string) => {
    setModalState({
      isOpen: true,
      mode: 'config',
      datasetId,
      sourceId,
      method,
    });
  };

  const openTestModal = (datasetId?: number, sourceId?: number, method?: string) => {
    setModalState((prev) => ({
      isOpen: true,
      mode: 'test',
      datasetId: datasetId ?? prev.datasetId,
      sourceId: sourceId ?? prev.sourceId,
      method: method ?? prev.method,
    }));
  };

  const switchMode = (mode: ApiRequestModalMode) => {
    setModalState((prev) => ({
      ...prev,
      mode,
    }));
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      mode: null,
      datasetId: null,
      sourceId: undefined,
      method: undefined,
    });
  };

  return (
    <ApiRequestModalContext.Provider
      value={{
        modalState,
        openConfigModal,
        openTestModal,
        switchMode,
        closeModal,
      }}
    >
      {children}
    </ApiRequestModalContext.Provider>
  );
};

export const useApiRequestModal = (): ApiRequestModalContextType => {
  const context = useContext(ApiRequestModalContext);
  if (!context) {
    throw new Error('useApiRequestModal must be used within an ApiRequestModalProvider');
  }
  return context;
};
